import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  alpha,
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { BoxIcon, RadioReceiverIcon, TriangleAlertIcon } from "lucide-react";

import {
  Alert,
  Autocomplete,
  Button,
  FieldLabel,
  Image,
  Modal,
} from "@repo/ui";
import {
  CompanyAvatar,
  EncodeSessionBody,
  PACKAGE_HANDLE_URL_RE,
  PackagePreview,
  Select,
  type SelectItem,
  ENCODABLE_FAMILIES,
  familyForTagShopProduct,
  familyForVariant,
} from "components";
import type { EncodeSessionTarget } from "components";
import { useEncodingDevice, useSnackbar } from "contexts";
import { useCanAccess, useEncodeSession } from "hooks";
import { useCompaniesQuery, useTagProducts, useUpdatePackage } from "hooks/api";
import { getErrorMessage } from "utils";
import { listedCompanies } from "utils/listedCompany";
import { companyLabel } from "utils/companyLabel";
import { parseSku } from "constants/tagCatalog/pricing";
import { isTagProduct } from "constants/tagShopProductTypes";
import type {
  TagShopProduct,
  TagShopProductVariant,
} from "hooks/api/useTagProducts";
import { api } from "services";

/** Same resolve host every handle write uses (EncodePackageHandleModal / AddPackagesModal). */
const HANDLE_VERIFY_HOST = "api.dev.vera.inc";

interface VariantOption {
  /** Keys the dropdown on the VARIANT — several products share a title+qty label. */
  id: string;
  label: string;
  product: TagShopProduct;
  variantId: string;
  variant: TagShopProductVariant;
  /** False for capabilities the desk reader cannot encode YET (Connect — VRG-252 gives it its
   *  own write+lock method). Flagged in the row, never used to disable it. */
  encodable: boolean;
  /**
   * What the tag DOES — "Verify", "Connect", "Verify + Tamper". Derived from the SKU's
   * capability segment, NOT from the chip family: `tag_family` (NTAG 213 / NTAG 424 DNA) is
   * internal fulfillment detail and is never surfaced in a picker. An operator chooses by
   * what the tag can do; which silicon delivers it is our problem, not a selection criterion.
   */
  capability: string | null;
}

interface PackageBoxModalProps {
  open: boolean;
  onClose: () => void;
  refetch?: () => void;
}

/**
 * VERA-790 — the combined Packages-page flow: register-or-resolve a box, then fill it. One modal
 * replaces the old "Add packages" (register) + "Add tags" (fill) split.
 *
 * Reader at top → "Scan a box to begin". Present a tag's label on the reader:
 *  - a plain 213 that ALREADY carries a `/pkg/<id>` handle → resolve that EXISTING box;
 *  - a BLANK 213 → auto-register it as a new package (write the handle, THEN create — VRG-330);
 *  - a 424 → rejected ("that's a product tag, not a box").
 * Then the encode section appears — "Scan tags for this package" — to fill the box with 424 tags.
 * "Next box" repeats. The resolve-or-register is done in ONE tap by the reader-helper
 * (`encodeHandle({ resolveOrRegister: true })`): it reads the tag and either returns the existing
 * handle or writes a new one.
 */
export const PackageBoxModal = (props: PackageBoxModalProps) => {
  const { open, onClose, refetch } = props;
  const theme = useTheme();
  const {
    isConnected,
    readerName,
    helperReachable,
    encodeHandle,
    cancelArm,
    isArming,
  } = useEncodingDevice();
  const { open: notification } = useSnackbar();

  // Box type (sets a registered box's capacity + product); optional — a bare box is unlimited.
  const [selectedVariant, setSelectedVariant] = useState<VariantOption | null>(
    null,
  );
  // The box currently being filled (resolved or just-registered). Null while awaiting a box scan.
  const [box, setBox] = useState<EncodeSessionTarget | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [boxesFilled, setBoxesFilled] = useState(0);
  // Running total of tags provisioned across ALL boxes this session (the per-box count resets each
  // Next-package; this accumulates so the Done toast can report the session total, not just the last box).
  const [sessionTags, setSessionTags] = useState(0);
  // "Re-arming" hint: the reader is briefly not listening between boxes (arm round-trip). Show it
  // only if that gap runs long enough to matter (>250ms) so a fast arm doesn't flicker the UI.
  const [showRearming, setShowRearming] = useState(false);
  useEffect(() => {
    if (!isArming) {
      setShowRearming(false);
      return;
    }
    const t = setTimeout(() => setShowRearming(true), 250);
    return () => clearTimeout(t);
  }, [isArming]);
  const [lock, setLock] = useState(false);
  // VRG-836 — write protection (rotate app key 0 + gate the chip's NDEF write/change rights
  // behind it). A SEPARATE opt-in from `lock`, defaulting OFF, so turning on today's lock can
  // never start rotating key 0. Reset with the session, like `lock`.
  const [writeProtect, setWriteProtect] = useState(false);

  // VRG-253 — the box's owning company. Assigned per PACKAGE (PATCH /packages/:id), the same
  // decision the Packages kebab's "Assign Package to Company" makes. Not a per-tag assign: the
  // scan-ownership guard falls back to the package's company (`getOwnership` prefers the tag's
  // own company_id, then the package's), so setting it here covers every tag in the box.
  const [companyAssigned, setCompanyAssigned] = useState<SelectItem | null>(
    null,
  );
  const [assigningCompany, setAssigningCompany] = useState(false);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const { isHasAccess: canAssignDirectly } = useCanAccess({
    action: "internal_assign_directly",
  });
  const updatePackage = useUpdatePackage();
  // VRG-808 — same opt-in as the other two assignment surfaces; see SetPackageCompanyModal.
  const { data: companiesData } = useCompaniesQuery(
    { limit: 200, includeDemo: true },
    { enabled: open && canAssignDirectly },
  );
  const companyOptions: SelectItem[] = useMemo(
    () =>
      listedCompanies(companiesData).map((c) => {
        // CompanyAvatar — the same rounded-square logo plate every other company surface
        // uses; see SelectItem['adornment'].
        return {
          value: c.id,
          label: c.name,
          adornment: <CompanyAvatar logo={c.logo} name={c.name} size={32} />,
          // VRG-808 — demo companies are admitted here, badged. Tag/package assignment to a
          // demo is the point: a prospect gets a physical product whose tag opens their own
          // branded certificate, and takeover is in-place, so the tag keeps working when they
          // sign. The badge is the safeguard the repository's blanket exclusion used to be.
          rightLabel: companyLabel(c.kind),
        };
      }),
    [companiesData?.data],
  );

  const scanRunningRef = useRef(false);
  const boxRef = useRef<EncodeSessionTarget | null>(null);
  useEffect(() => {
    boxRef.current = box;
  }, [box]);
  const selectedVariantRef = useRef(selectedVariant);
  useEffect(() => {
    selectedVariantRef.current = selectedVariant;
  }, [selectedVariant]);

  const session = useEncodeSession();
  const {
    provisioned,
    quarantined,
    packageTagCount,
    stopSession,
    resetSession,
    runSession,
    seedPackageCount,
  } = session;
  // Next-package / Done confirmation when tags are still unresolved (quarantined). Holds which
  // action the user is confirming, or null.
  const [confirmProceed, setConfirmProceed] = useState<null | "next" | "done">(
    null,
  );

  // A scanned box that ALREADY holds tags, awaiting the operator's choice: fill it, or make
  // this chip a new package instead. VRG-330 — `pendingId` is the id RESERVED for the scan (no
  // row exists for it); "Start a new package" writes it to the chip and creates it, and the adopt
  // branch just drops it.
  const [adoptPrompt, setAdoptPrompt] = useState<null | {
    existing: EncodeSessionTarget;
    already: number;
    pendingId: string;
    url: string;
  }>(null);

  const { data: tagProductsData } = useTagProducts(
    { limit: 100, offset: 0 },
    { enabled: open },
  );
  const variantOptions: VariantOption[] = useMemo(() => {
    const opts: VariantOption[] = [];
    // Tags only. The shop's catalog also carries the desk reader (Shopify productType
    // "Hardware"), which is not a tag type a package can be filled with.
    for (const product of (tagProductsData?.products ?? []).filter(
      isTagProduct,
    )) {
      for (const variant of product.variants ?? []) {
        opts.push({
          // `id` so the dropdown keys on the VARIANT, not the label. AutoComplete falls back
          // to `option.id ?? option.sku ?? label`, and several products share a title+qty
          // ("Fabric Label · Qty 500"), so keying on the label produced React duplicate-key
          // warnings — which explicitly permit children being "duplicated and/or omitted",
          // i.e. a selector where picking one option could yield another.
          id: variant.id,
          label: `${product.title} · Qty ${variant.quantity || 1}`,
          product,
          variantId: variant.id,
          variant,
          // Whether the desk reader can encode this variant AT ALL. Only NTAG 424 DNA carries
          // the SDM/SUN capability an encode writes; a Connect tag is a plain NDEF chip and
          // never will. Computed here so the block lands at SELECTION rather than after a
          // package exists and a handle has been written to a physical chip (VRG-251).
          encodable: ENCODABLE_FAMILIES.includes(
            familyForVariant(variant) as any,
          ),
          capability: variant.sku
            ? (parseSku(variant.sku)?.capability ?? null)
            : null,
        });
      }
    }
    return opts;
  }, [tagProductsData]);

  const targetCount = box?.targetCount;
  const bigCount = packageTagCount != null ? packageTagCount : provisioned;
  const isPackageFilled = targetCount != null && bigCount >= targetCount;

  // Resolve an EXISTING box (GET /packages/:id) into a fill target (family + capacity from its
  // tag-shop product), mirroring PackagesPage's packageRowToTarget / BulkEncodeTagsModal.
  const resolveBox = useCallback(
    async (packageId: string): Promise<EncodeSessionTarget | null> => {
      try {
        const resp = await api.get(`/packages/${packageId}`);
        const status = resp?.status;
        const pkg = resp?.data;
        if (
          !pkg?.id ||
          (typeof status === "number" && (status < 200 || status >= 300))
        )
          return null;
        const tsp = pkg.tag_shop_product ?? null;
        return {
          kind: "package",
          id: pkg.id,
          name: pkg.name,
          family: familyForTagShopProduct(tsp),
          productName: tsp?.title ?? undefined,
          productSku: tsp?.selected_variant?.sku ?? tsp?.sku ?? undefined,
          productTitle: tsp?.selected_variant?.title ?? tsp?.title ?? undefined,
          productImage:
            tsp?.selected_variant?.image ?? tsp?.images?.[0] ?? undefined,
          lock: false,
          targetCount: tsp?.variants?.find(
            (v: any) => v?.id === tsp?.selected_variant?.id,
          )?.quantity,
          // How many tags this box ALREADY holds — drives the adopt-or-replace prompt below.
          // A box with tags in it is where "did I mean this one?" is a real question.
          //
          // `tags_count` is computed server-side (countByPackageId). This deliberately does NOT
          // read `pkg.tags`: GET /packages/:id selects the packages row alone and never joined
          // tags, so that field is always undefined here — the platform's `Package` type
          // declares `tags: Tag[]`, which describes a different payload and is what made the
          // first attempt silently count 0 and never show the prompt.
          existingTagCount:
            typeof pkg.tags_count === "number" ? pkg.tags_count : 0,
          // VRG-253 — an ALREADY-registered box may already belong to a company. Carry it through so
          // the assign step shows the current owner instead of reading as unassigned (which would
          // invite re-assigning stock that is already someone's).
          companyId: pkg.company_id ?? null,
        } as EncodeSessionTarget & {
          existingTagCount: number;
          companyId: string | null;
        };
      } catch {
        return null;
      }
    },
    [],
  );

  /**
   * VRG-330 — bring a reserved id into existence as a real package, already bound to the chip that
   * was just written. One request, so "created" and "bound to a chip" are the same event and a
   * package with no chip cannot be produced here at all.
   *
   * Returns the failure REASON rather than swallowing it. Every cleanup on the old create-first
   * path was a fire-and-forget `.catch(() => undefined)`, which is what made this class of problem
   * invisible: `DELETE /packages/:id` is admin-only, so a non-admin operator's rollback failed
   * silently on every cycle and each one left a row. There is nothing to roll back now, and the
   * one remaining failure — a create that doesn't land — is reported to the operator with the
   * action that fixes it.
   */
  const createBoundPackage = useCallback(
    async (
      id: string,
      handleTagUid: string,
      variant: VariantOption | null,
    ): Promise<{
      pkg: { id: string; name: string } | null;
      error: string | null;
    }> => {
      try {
        const resp = await api.post("/packages", {
          id,
          handleTagUid,
          ...(variant
            ? {
                tagShopProductData: JSON.stringify(variant.product),
                variantId: variant.variantId,
              }
            : {}),
        });
        const status = resp?.status;
        const data = resp?.data;
        // `api.post` RESOLVES on a 4xx (the axios interceptor only rejects on a total network
        // failure), so the status has to be checked explicitly or a rejected registration reads
        // as a success with an undefined package.
        if (
          !data?.success ||
          !data.package ||
          (typeof status === "number" && (status < 200 || status >= 300))
        ) {
          return { pkg: null, error: data?.message || null };
        }
        return {
          pkg: { id: data.package.id, name: data.package.name },
          error: null,
        };
      } catch (error) {
        return { pkg: null, error: getErrorMessage(error) || null };
      }
    },
    [],
  );

  // The scan-a-box loop: mint an id for the URL, arm a resolve-or-register handle write, and
  // create the package only once a chip is bound to it. Loops (re-arming) until a box
  // resolves/registers or it's stopped.
  const runScanBox = useCallback(async () => {
    if (scanRunningRef.current || boxRef.current) return;
    scanRunningRef.current = true;
    setScanning(true);
    setScanError(null);
    while (scanRunningRef.current && !boxRef.current) {
      const variant = selectedVariantRef.current;
      // 1. VRG-330 — RESERVE an id locally. The handle URL embeds the package id, which is why a
      //    package used to be created here, before the reader was even armed: a real, listable
      //    row for a box nobody had presented, for the whole 310s arm window, deleted afterwards
      //    by a fire-and-forget DELETE that nothing checked. In the loop below that create ran on
      //    EVERY iteration, so a modal left open burned (and then discarded) one package per arm
      //    timeout, indefinitely.
      //
      //    A UUID minted here costs nothing and commits to nothing. Only step 3 — after a chip is
      //    physically bound — turns it into a row, so the create-then-maybe-delete shape, and
      //    every orphan it could leave, is gone rather than narrowed.
      //
      //    No non-secure-context fallback for `crypto.randomUUID`: the reader-helper's origin
      //    allow-list is localhost / 127.0.0.1 (plus a future https production origin), all of
      //    which ARE secure contexts, so anywhere this loop can run the API is defined. A fallback
      //    would also have to emit a real UUID — the API rejects any id that isn't one.
      const pendingId = crypto.randomUUID();
      const url = `https://${HANDLE_VERIFY_HOST}/pkg/${pendingId}`;

      // 2. Resolve-or-register the presented tag in one tap.
      const result = await encodeHandle({ url, resolveOrRegister: true });
      // Modal closed / loop stopped while armed. Nothing to undo — that is the point.
      if (!scanRunningRef.current) break;

      // 3. Create the package, already bound to the presented chip, and start filling it.
      //    Returns false when the create failed, so the caller keeps scanning rather than
      //    returning into a state where the loop has exited but its flags still say it is running.
      const registerNewBox = async (uid: string): Promise<boolean> => {
        const { pkg, error } = await createBoundPackage(
          pendingId,
          uid,
          variant,
        );
        if (!pkg) {
          // The chip carries `/pkg/<pendingId>` but no such package exists — the one failure this
          // ordering can produce, and the mirror of the one it removes. It is the better failure:
          // an unregistered chip is inert (nothing lists it, nothing counts it), and it is
          // recoverable — re-presenting the box lands in the deleted-package branch below, which
          // re-registers it. An orphan ROW was neither.
          setScanError(
            error ||
              "The label was written but the package could not be created. Present the box again to finish registering it.",
          );
          return false;
        }
        setBox({
          kind: "package",
          id: pkg.id,
          name: pkg.name,
          family: familyForVariant(variant?.variant),
          productName: variant?.product.title ?? undefined,
          productSku: variant?.variant.sku ?? variant?.product.sku ?? undefined,
          productTitle: variant?.product.title ?? undefined,
          productImage:
            variant?.variant.images?.[0] ??
            variant?.product.images?.[0] ??
            undefined,
          lock: false,
          targetCount: variant?.variant.quantity || undefined,
        });
        // Box found → the scan loop is done until "Next package" re-arms it. Clear the running
        // flag (the early `return`s below skip the post-loop cleanup) so the arm effect can restart.
        scanRunningRef.current = false;
        setScanning(false);
        return true;
      };

      if (result.existing && result.existingUrl) {
        // The tag already carries a /pkg handle. Resolve that box if it still exists.
        const match = PACKAGE_HANDLE_URL_RE.exec(result.existingUrl);
        const existing = match ? await resolveBox(match[1]) : null;
        if (existing) {
          const already = (existing as any).existingTagCount ?? 0;
          // Also ask when the resolved package CANNOT be encoded, even with zero tags.
          // Adopting it silently strands the operator: the session opens, reports "no
          // verification chip", and offers no way out except closing the modal — while the
          // box's chip still points at that package, so re-scanning lands right back there.
          // The override path is the only escape, so it has to be offered.
          const deadEnd =
            !existing.family ||
            !ENCODABLE_FAMILIES.includes(existing.family as any);
          scanRunningRef.current = false;
          setScanning(false);
          if (already > 0 || deadEnd) {
            // ASK before adopting a box that already has tags.
            //
            // Silently adopting it is what made a variant selection appear to vanish: the
            // session's chip family comes from the PACKAGE's stored tag-shop product
            // (familyForTagShopProduct), not from the variant chosen in the configure step,
            // so resolving onto an existing box quietly discards that choice — and when the
            // box has no product set, the only visible symptom is a downstream
            // "Missing tag type" with nothing linking it back to the cause.
            //
            // VRG-330 — `pendingId` is a RESERVED id, not a row: "Start a new package" creates it
            // (after overwriting the chip), and the adopt branch simply never uses it. Neither
            // answer, nor dismissing the prompt, has anything to delete any more.
            setAdoptPrompt({ existing, already, pendingId, url });
            return;
          }
          // Empty box — an unambiguous fill, nothing to ask about.
          setBox(existing);
          return;
        }
        // The handle points at a package that no longer exists (deleted, or written by a
        // registration whose create never landed). Don't dead-end: re-register by overwriting the
        // tag with the id reserved above.
        // Copy says "isn't registered", not "was deleted": VRG-330 gives this branch a second,
        // equally likely cause — a chip written by a registration whose create never landed — and
        // naming only the deleted case would misdescribe it.
        setScanError(
          "This box isn’t registered to a package. Keep it on the reader to re-register it.",
        );
        const rewrite = await encodeHandle({ url });
        if (!scanRunningRef.current) break;
        if (rewrite.ok && rewrite.uid) {
          setScanError(null);
          if (await registerNewBox(rewrite.uid)) return;
          continue;
        }
        // The overwrite didn't land (lifted too soon / not a box). Keep scanning.
        const rmsg = rewrite.error || "";
        if (rewrite.wrongTagType || /not_a_box/i.test(rmsg)) {
          setScanError(
            "That's a product tag, not a box. Present a box's own label.",
          );
        } else if (
          !rewrite.cancelled &&
          !/no tag|in time|present/i.test(rmsg)
        ) {
          setScanError(rmsg || null);
        }
        continue;
      }

      if (result.ok && result.uid) {
        // Blank tag → written. NOW create the package, bound to that chip.
        if (await registerNewBox(result.uid)) return;
        continue;
      }

      // Not a box, or nothing presented. Nothing was created, so nothing is discarded.
      const msg = result.error || "";
      if (result.wrongTagType || /not_a_box/i.test(msg)) {
        setScanError(
          "That's a product tag, not a box. Present a box's own label.",
        );
      } else if (!result.cancelled && !/no tag|in time|present/i.test(msg)) {
        setScanError(msg || null);
      } else {
        setScanError(null);
      }
      // loop re-arms
    }
    scanRunningRef.current = false;
    setScanning(false);
  }, [encodeHandle, resolveBox, createBoundPackage]);

  // Start the fill session once a box is resolved/registered.
  useEffect(() => {
    if (!box) return;
    seedPackageCount(box.id);
    runSession({
      target: { kind: "package", id: box.id },
      family: box.family,
      lock,
      writeProtect,
      productSku: box.productSku,
      productTitle: box.productTitle,
      productImage: box.productImage,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box?.id]);

  // Close → tear everything down.
  useEffect(() => {
    if (!open) {
      scanRunningRef.current = false;
      // VRG-330 — withdraw the outstanding arm. Clearing `scanRunningRef` alone did not stop the
      // loop: it was parked in `await encodeHandle(...)`, which does not return for up to 310s, so
      // the closure stayed alive holding this modal's state and a late result could still be acted
      // on. This resolves it now. It does NOT disarm the reader — see `cancelArm`'s doc comment.
      cancelArm();
      stopSession();
      resetSession();
      setBox(null);
      setScanning(false);
      setScanError(null);
      setBoxesFilled(0);
      setSessionTags(0);
      setSelectedVariant(null);
      setLock(false);
      setWriteProtect(false);
      setCompanyAssigned(null);
      setCompanyError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Prefill the assign step from a resolved box that ALREADY belongs to a company. Keyed on the
  // box id AND the options list because the companies query may still be in flight when a box
  // resolves — without the options dependency the owner would silently never appear.
  const boxCompanyId = (box as any)?.companyId ?? null;
  useEffect(() => {
    if (!boxCompanyId) return;
    const match = companyOptions.find((o) => o.value === boxCompanyId);
    if (match) setCompanyAssigned(match);
  }, [boxCompanyId, companyOptions]);

  // The selected variant's chip family, taken ONLY from its explicit tag_family (no inference).
  // Null → the variant has no tag type set in Shopify, so we won't arm (can't encode a guessed
  // family, and registering a box we can't fill is pointless).
  const selectedFamily = selectedVariant
    ? familyForVariant(selectedVariant.variant)
    : null;

  // A package variant is REQUIRED here (a new package needs its product + quantity), AND it must
  // carry a tag type — so we don't arm the reader until a variant WITH a tag_family is picked.
  useEffect(() => {
    if (
      open &&
      selectedVariant &&
      selectedFamily &&
      !box &&
      !scanRunningRef.current
    )
      runScanBox();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedVariant, selectedFamily, box]);

  // VRG-253 — assignment is committed IMMEDIATELY, not deferred to Done like EncodeTagsModal's
  // version. The difference is what exists at that moment: there, the tags are only encoded and
  // deferring keeps X a true no-op; here the package is already created and its handle already
  // written to a physical chip, so `company_id` is one reversible field on a record that exists
  // either way. Committing on pick also avoids carrying pending state across "Next package",
  // where a deferred assign would have to be flushed at two more exits.
  const handleAssignCompany = useCallback(
    async (opt: SelectItem | null) => {
      if (!box || !opt) return;
      setAssigningCompany(true);
      setCompanyError(null);
      try {
        await updatePackage.mutateAsync({
          id: String(box.id),
          company_id: opt.value,
        });
        setCompanyAssigned(opt);
      } catch (error) {
        setCompanyError(
          getErrorMessage(error) || "Could not assign the package",
        );
      } finally {
        setAssigningCompany(false);
      }
    },
    [box, updatePackage],
  );

  // NOTE: there is deliberately NO unassign here. An earlier version had a preview-plus-
  // "Change" affordance and a handler that PATCHed company_id to null, but the picker is a
  // bare Select rendering only company options — MUI can only emit a child's value, so the
  // clear branch was unreachable and the handler was dead code documenting a UI that was not
  // in the tree. Reassignment works; unassigning is the Packages kebab's job.

  const doNextBox = useCallback(() => {
    stopSession();
    setBoxesFilled((c) => c + (provisioned > 0 ? 1 : 0));
    setSessionTags((t) => t + provisioned);
    resetSession();
    setBox(null);
    setScanError(null);
    setCompanyAssigned(null);
    setCompanyError(null);
    // Clearing `box` re-arms the scan loop via the variant-armed effect (box → null, variant still
    // set, scan flag cleared) — no manual re-arm, which used to race the box-ref reset and no-op.
  }, [stopSession, resetSession, provisioned]);

  const doDone = useCallback(() => {
    // Confirm the SESSION total (the running list is gone once the modal closes): every tag across
    // every box filled this session, and how many packages that spanned.
    const totalTags = sessionTags + provisioned;
    const totalBoxes = boxesFilled + (provisioned > 0 ? 1 : 0);
    if (totalTags > 0) {
      const tagsLabel = `${totalTags} tag${totalTags === 1 ? "" : "s"}`;
      // Name the company when there is one. The assignment is the consequential half of this
      // action — `getOwnership` falls back to the package's company, so it decides which
      // company's scan sessions may use these tags at all — and it was invisible in the one
      // message confirming the work. No company → say nothing rather than "assigned to nobody".
      //
      // ONLY on the single-package branch, and that is not a shortcut: `companyAssigned` is
      // reset by "Next package", so at Done it describes the CURRENT box alone. Appending it to
      // the multi-package line would assert that every package this session went to that
      // company, which is precisely what the code does not know.
      const assignedSuffix = companyAssigned
        ? `, assigned to ${companyAssigned.label}`
        : "";
      // One package → name it (its scannable handle ID, not the product title). Multiple → report
      // the package count instead of naming each.
      const title =
        totalBoxes === 1 && provisioned > 0 && box
          ? `${tagsLabel} added to ${box.name}${assignedSuffix}`
          : `${tagsLabel} added across ${totalBoxes} package${totalBoxes === 1 ? "" : "s"}`;
      notification({ type: "success", title });
    }
    refetch?.();
    onClose();
  }, [
    provisioned,
    sessionTags,
    box,
    boxesFilled,
    companyAssigned,
    notification,
    refetch,
    onClose,
  ]);

  // Guard Next-package / Done: if any tags are still unresolved (quarantined — already elsewhere,
  // full, or failed), CONFIRM before proceeding rather than silently leaving them. A soft confirm,
  // not a hard block: some quarantines (a tag legitimately in another package) can't be "resolved"
  // here, so trapping the operator would be wrong.
  const handleNextBox = useCallback(() => {
    if (quarantined > 0) {
      setConfirmProceed("next");
      return;
    }
    doNextBox();
  }, [quarantined, doNextBox]);

  const handleDone = useCallback(() => {
    if (quarantined > 0) {
      setConfirmProceed("done");
      return;
    }
    doDone();
  }, [quarantined, doDone]);

  /** Fill the box that was scanned, keeping ITS product/tag-type. The reserved id is simply
   *  never used — VRG-330, there is no throwaway row to delete. */
  const adoptExisting = useCallback(() => {
    if (!adoptPrompt) return;
    setBox(adoptPrompt.existing);
    setAdoptPrompt(null);
  }, [adoptPrompt]);

  /** Make this chip a NEW package instead, using the variant chosen in the configure step.
   *  Overwrites the chip's handle with the reserved id, then creates that package bound to the
   *  chip — the same write-then-create order the scan loop uses. Needs the chip still on the
   *  reader, so it surfaces the usual "keep it on the reader" guidance. */
  const startNewPackage = useCallback(async () => {
    if (!adoptPrompt) return;
    const { pendingId, url } = adoptPrompt;
    setAdoptPrompt(null);
    setScanError("Keep the box on the reader to register it as a new package.");
    const rewrite = await encodeHandle({ url });
    if (rewrite.ok && rewrite.uid) {
      const { pkg, error } = await createBoundPackage(
        pendingId,
        rewrite.uid,
        selectedVariantRef.current,
      );
      if (pkg) {
        setScanError(null);
        const fresh = await resolveBox(pkg.id);
        if (fresh) {
          setBox(fresh);
          return;
        }
      }
      // The chip now points at this id but the package isn't there. Say so, and re-arm: presenting
      // the box again lands in the scan loop's deleted-package branch, which re-registers it.
      setScanError(
        error ||
          "The label was written but the package could not be created. Present the box again.",
      );
      if (!scanRunningRef.current && !boxRef.current) runScanBox();
      return;
    }
    // The overwrite didn't land (lifted too soon). Don't strand the operator on a dead prompt —
    // nothing was created, so just let them scan again.
    setScanError(
      rewrite.error || "That didn't register. Present the box again.",
    );
    // Re-arm, or that instruction is one the code cannot honour — same exited-loop trap as
    // dismissAdoptPrompt above.
    if (!scanRunningRef.current && !boxRef.current) runScanBox();
  }, [adoptPrompt, encodeHandle, resolveBox, createBoundPackage]);

  /**
   * Dismissing the adopt prompt (X / Escape / backdrop) — NOT one of its two answers.
   *
   * Two things had to happen here and neither did. `runScanBox` exits its while-loop on the
   * existing-box branch (scanRunningRef=false, then `return`), and the re-arm effect only
   * watches [open, selectedVariant, selectedFamily, box] — none of which dismissing changes.
   * So the modal went back to saying "Present a box on the reader" while the reader was NOT
   * armed, and tapping boxes did nothing until the variant was re-picked. The throwaway
   * package created to mint the handle URL was also never cleaned up, so every dismissal
   * orphaned an empty package row into the Packages list. VRG-330 removed the second half at
   * the source — the id is reserved, not created — so only the re-arm is left to do here.
   */
  const dismissAdoptPrompt = useCallback(() => {
    setAdoptPrompt(null);
    setScanError(null);
    // Re-arm explicitly: nothing in the effect's dependency list changed.
    if (!scanRunningRef.current && !boxRef.current) runScanBox();
  }, [runScanBox]);

  const proceedConfirmed = useCallback(() => {
    const action = confirmProceed;
    setConfirmProceed(null);
    if (action === "next") doNextBox();
    else if (action === "done") doDone();
  }, [confirmProceed, doNextBox, doDone]);

  // "Change" the picked variant (before a box is scanned): stop the scan loop and return the picker.
  const handleChangeVariant = useCallback(() => {
    scanRunningRef.current = false;
    // VRG-330 — same reason as the close teardown: clearing the flag alone leaves the loop parked
    // in its arm for up to 310s, so picking a different variant would not actually re-arm with it
    // until the old arm timed out.
    cancelArm();
    setScanning(false);
    setSelectedVariant(null);
    setScanError(null);
  }, [cancelArm]);

  return (
    <>
      <Modal
        title={box ? "Add tags" : "Add packages"}
        titleAdornment={
          // Current package ordinal this session (package 2 onward) — e.g. "(2)" after "Next package".
          boxesFilled > 0 ? (
            <Box
              sx={{
                minWidth: 22,
                height: 22,
                px: 1,
                borderRadius: "11px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.brand[500], 0.12),
                color: theme.palette.brand[600],
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {boxesFilled + 1}
            </Box>
          ) : undefined
        }
        open={open}
        onClose={handleDone}
        actions={
          <>
            {box && (
              <Button variant="secondary" onClick={handleNextBox}>
                Next package
              </Button>
            )}
            <Button variant="primary" onClick={handleDone}>
              Done
            </Button>
          </>
        }
      >
        <Stack spacing={4}>
          {/* VERA-455 — the catalog below can be a DEV MOCK, and until this banner existed there was
            no way to tell. The API returned 200 with a normal-shaped payload, so synthetic SKUs
            (30-N-SOLID-100) rendered exactly like real ones and the only symptom was a downstream
            "Missing tag type. Set it on this variant in Shopify" for a SKU Shopify has never had.
            The underlying failure was logged 19 times in 30 days and read by nobody. */}
          {tagProductsData?.mocked && (
            <Alert
              severity="warning"
              description="These are placeholder products, not your Shopify catalog. Vera can't reach Shopify right now, so these SKUs do not exist in your store. Packages created here are real, but the product they name is not."
            />
          )}
          {/* Package variant — sets a newly-registered box's capacity + product. Required from the
            Packages page (a new package needs its product + quantity). Locked once a box is active. */}
          {!box && (
            // NOT a `Stack spacing`: FieldLabel owns the label→control gap (§31), and a Stack
            // would add its own on top of FieldLabel's `mb`, so the canonical 8px would silently
            // become 8 + whatever this container chose. A plain Box lets the label own it.
            <Box>
              <FieldLabel label="Package variant" />
              {selectedVariant ? (
                // Show the picked variant's product preview (title + Quantity + SKU) with a Change to
                // re-pick — same card the scanned box uses below, so the two steps read consistently.
                <PackagePreview
                  title={selectedVariant.product.title}
                  image={
                    selectedVariant.variant.images?.[0] ??
                    selectedVariant.product.images?.[0]
                  }
                  quantity={selectedVariant.variant.quantity}
                  sku={
                    selectedVariant.variant.sku ?? selectedVariant.product.sku
                  }
                  onChange={handleChangeVariant}
                />
              ) : (
                <Autocomplete
                  fullWidth
                  hideStartIcon
                  autoHighlight
                  options={variantOptions}
                  value={selectedVariant}
                  onSelect={(opt: VariantOption | null) =>
                    setSelectedVariant(opt)
                  }
                  placeholder="Select a tag product and quantity"
                  getOptionLabel={(opt: VariantOption) => opt?.label ?? ""}
                  isOptionEqualToValue={(a: VariantOption, b: VariantOption) =>
                    a?.variantId === b?.variantId
                  }
                  renderOption={(opt: VariantOption) => {
                    const img =
                      opt.variant.images?.[0] ??
                      opt.product.images?.[0] ??
                      null;
                    return (
                      <Stack
                        direction="row"
                        spacing={3}
                        alignItems="center"
                        sx={{ py: 0.5, minWidth: 0, width: "100%" }}
                      >
                        {img ? (
                          <Image
                            src={img}
                            alt={opt.product.title}
                            fit="cover"
                            sx={(theme) => ({
                              width: 40,
                              height: 40,
                              borderRadius: "6px",
                              border: `1px solid ${theme.palette.grey[200]}`,
                              flexShrink: 0,
                            })}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "6px",
                              bgcolor: theme.palette.grey[50],
                              border: `1px solid ${theme.palette.grey[200]}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <BoxIcon
                              strokeWidth={1}
                              size={18}
                              color={theme.palette.grey[400]}
                            />
                          </Box>
                        )}
                        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {opt.label}
                          </Typography>
                          {/* Selectable, but flagged. A Connect tag is a real product and its
                            package is a real package — it simply needs a different encode
                            method (write + lock, no SDM), which does not exist yet (VRG-252).
                            Disabling these was wrong: it asserted "never", when the truth is
                            "not yet". */}
                          {/* Capability, not the SKU and never the chip family. The SKU was a
                            code the operator has to decode ("VERA-STK-VER-25-100"); the
                            capability is the thing they are actually choosing between. */}
                          <Typography
                            variant="body3"
                            color="text.secondary"
                            noWrap
                          >
                            {[
                              opt.capability,
                              !opt.encodable
                                ? "Encoding not supported yet"
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </Typography>
                        </Stack>
                      </Stack>
                    );
                  }}
                />
              )}
            </Box>
          )}

          {boxesFilled > 0 && (
            <Typography variant="body3" color="text.secondary">
              {boxesFilled} box{boxesFilled === 1 ? "" : "es"} filled this
              session
            </Typography>
          )}

          {box ? (
            <>
              {/* The scanned/registered package — product title + Quantity + SKU (not the handle,
                which is now dropped from the modal title) — above the tags-scanned card, under a
                "Package" label for parity with the "Package variant" step. */}
              <Box>
                <FieldLabel label="Package" />
                <PackagePreview
                  title={box.productName ?? box.productTitle ?? box.name}
                  image={box.productImage}
                  quantity={box.targetCount}
                  sku={box.productSku}
                />
              </Box>

              {/* VRG-253 "Assign to company" — the same capability as the Packages kebab, offered
                here so a box can be handed to its owner in the flow that registers it, instead of
                requiring a second trip to the list. Gated on the permission (§24/§27): hidden
                rather than disabled, because an operator without it has no path to the capability
                at all, so a disabled picker would be pure clutter in a flow they use constantly. */}
              {canAssignDirectly && (
                // NO card. This is a field, and it sits beside "Package variant" / "Package",
                // which are plain label-above-control. Wrapping only this one in a bordered box
                // made it read as a different KIND of thing — a panel among fields — when all
                // three are peers in the same form. Same reason it is not a `Stack spacing`:
                // FieldLabel owns the label→control gap (§31).
                <Box>
                  <FieldLabel
                    label="Assign to company"
                    adornment="(optional)"
                  />

                  {/* The SHARED Select — the same company picker the Assign to Company modal
                    uses. This was briefly a hand-rolled Autocomplete + CompanyAvatar (copied
                    from EncodeTagsModal, which still hand-rolls its own); two bespoke pickers
                    for one job is exactly the drift working-conventions §28/§36 exist to stop. */}
                  <Select
                    options={companyOptions}
                    value={companyAssigned?.value ?? ""}
                    placeholder="Select a company"
                    disabled={assigningCompany}
                    onChange={(value) => {
                      const id = typeof value === "string" ? value : "";
                      handleAssignCompany(
                        companyOptions.find((o) => o.value === id) ?? null,
                      );
                    }}
                  />

                  {/* Below-control status: its own top margin rather than a container's spacing,
                    for the same reason as above — a spacing container here would reach back up
                    and change the label→control gap too. */}
                  {assigningCompany && (
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      sx={{ mt: 2 }}
                    >
                      <CircularProgress
                        size={18}
                        thickness={4}
                        sx={{ color: theme.palette.brand[500] }}
                      />
                      <Typography variant="body3" color="text.secondary">
                        Assigning…
                      </Typography>
                    </Stack>
                  )}

                  {companyError && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="warning" description={companyError} />
                    </Box>
                  )}
                </Box>
              )}

              <EncodeSessionBody
                session={session}
                helperReachable={helperReachable}
                isConnected={isConnected}
                readerName={readerName}
                lock={lock}
                setLock={setLock}
                writeProtect={writeProtect}
                setWriteProtect={setWriteProtect}
                bigCount={bigCount}
                targetCount={targetCount}
                isPackageFilled={isPackageFilled}
                counterLabel="in this package"
              />
            </>
          ) : (
            <>
              {/* Reader row (shared style) + scan-a-box placeholder. */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={3}
                sx={{
                  border: `1px solid ${theme.palette.primary[100]}`,
                  borderRadius: 2,
                  px: 4,
                  py: 3,
                }}
              >
                <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2">
                    {helperReachable && isConnected
                      ? readerName || "Vera Desk Reader"
                      : helperReachable
                        ? "No reader detected"
                        : "Reader helper not running"}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.5 }}
                  >
                    Present a box's own label on the reader. A blank tag is
                    registered as a new box.
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ flexShrink: 0 }}
                >
                  {helperReachable && isConnected && (
                    <Typography
                      variant="body3"
                      color={theme.palette.brand[500]}
                    >
                      Connected
                    </Typography>
                  )}
                  <RadioReceiverIcon
                    strokeWidth={1}
                    color={
                      helperReachable && isConnected
                        ? theme.palette.brand[500]
                        : theme.palette.text.disabled
                    }
                  />
                </Stack>
              </Stack>

              <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
                {showRearming && selectedFamily ? (
                  <CircularProgress
                    size={28}
                    thickness={4}
                    sx={{ color: theme.palette.brand[500] }}
                  />
                ) : (
                  <BoxIcon
                    strokeWidth={1}
                    size={32}
                    color={theme.palette.text.disabled}
                  />
                )}
                <Typography variant="body3" color="text.secondary">
                  {!selectedVariant
                    ? "Select a package variant to begin."
                    : !selectedFamily
                      ? "Missing tag type. Set it on this variant in Shopify, then try again."
                      : showRearming
                        ? "Re-arming reader…"
                        : scanning
                          ? "Scan a box to begin."
                          : "Present a box on the reader."}
                </Typography>
                {scanError && (
                  <Alert severity="warning" description={scanError} />
                )}
              </Stack>
            </>
          )}
        </Stack>
      </Modal>

      {/* A scanned box that already holds tags: adopt it, or make this chip a new package.
        Deliberately NOT silent — see the setAdoptPrompt call site for why.

        STAYS A MODAL: the flow cannot continue until this is answered, and continuing
        silently is the exact bug being fixed. That is what a modal is for.

        TWO BUTTONS, NOT A RADIO + CONTINUE: this is a binary choice with self-describing
        labels, so a selector would add a click and a step without adding information. The
        real problem with the first version was not the button count — it was that neither
        button was primary and the consequence lived in a paragraph. Both are fixed by
        making the VIABLE option primary and disabling the one that cannot work, so the
        choice is legible from the controls alone (§26 one primary, §27 disable + explain
        rather than hide). */}
      <Modal
        open={!!adoptPrompt}
        // No glyph in the title: the warning Alert below already carries one, and two warning
        // marks in one small dialog is redundant emphasis rather than clearer signalling.
        title={
          adoptPrompt?.already
            ? "This box already has tags"
            : "This package already exists"
        }
        onClose={dismissAdoptPrompt}
        actions={
          <>
            {/* BOTH secondary, deliberately. These are two alternatives, not an action and an
              escape — there is no "recommended" one, and promoting either would nudge a
              choice only the operator can make. This is the case working-conventions §26
              carves out: one primary per header assumes a primary EXISTS. */}
            <Button variant="secondary" onClick={startNewPackage}>
              Override as new package
            </Button>
            <Button variant="secondary" onClick={adoptExisting}>
              Add to this package
            </Button>
          </>
        }
      >
        <Stack spacing={4}>
          <Alert severity="warning" variant="outlined">
            {adoptPrompt?.already ? (
              <>
                <strong>{adoptPrompt.existing.name}</strong> already has{" "}
                {adoptPrompt.already} tag
                {adoptPrompt.already === 1 ? "" : "s"}.
              </>
            ) : (
              <>
                It is already registered as{" "}
                <strong>{adoptPrompt?.existing.name}</strong>.
              </>
            )}
          </Alert>
          {/* Only when there is something to warn ABOUT. A package with a tag type set is an
            ordinary either/or and needs no banner. */}
          {adoptPrompt && !adoptPrompt.existing.family && (
            <Alert severity="warning" variant="outlined">
              This package has no tag type set, so tags can’t be encoded into
              it. Override it as a new package to use the variant you selected.
            </Alert>
          )}
        </Stack>
      </Modal>

      {/* Confirm before proceeding with unresolved (quarantined) tags — soft confirm, not a hard block. */}
      <Modal
        open={!!confirmProceed}
        title="Unresolved tags"
        onClose={() => setConfirmProceed(null)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirmProceed(null)}>
              Back
            </Button>
            <Button variant="primary" onClick={proceedConfirmed}>
              Proceed anyway
            </Button>
          </>
        }
      >
        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
          {quarantined} tag{quarantined === 1 ? "" : "s"}{" "}
          {quarantined === 1 ? "wasn't" : "weren't"} added to this package.{" "}
          {quarantined === 1 ? "It's" : "They're"} already in another package,
          full, or failed to write. Resolve {quarantined === 1 ? "it" : "them"}{" "}
          first (e.g. "Add here instead"), or proceed and leave{" "}
          {quarantined === 1 ? "it" : "them"} as{" "}
          {quarantined === 1 ? "is" : "they are"}.
        </Typography>
      </Modal>
    </>
  );
};
